import React, { useEffect, useId, useMemo, useRef } from 'react';
import {
  useAimProviderCategories,
  useAimProviderFacts,
  useAimProviderCitations,
  useAimProviderCoverage,
} from '../lib/aimQueries';
import ProviderColorDot from '../primitives/ProviderColorDot';
import RatingColorBar from '../primitives/RatingColorBar';
import AimEmpty, { AimLoading } from '../primitives/AimEmpty';
import { colorFor } from '../lib/providerColors';
import { prettifyCategory } from '../lib/categoryLabel';
import { providerCategoryRows, providerCitationRows, providerFactsView } from '../lib/providerDetail';
import { coverageFor, sectionEmptyMessage, allSectionsFailed } from '../lib/providerCoverage';
import { NO_DATA_MESSAGE, isCollectionFailed } from '../lib/dataStatus';
import { isPending } from '../lib/queryState';
import useFocusTrap from '../../../hooks/useFocusTrap';
import AimPagination from '../primitives/AimPagination';
import usePagedRows from '../lib/usePagedRows';

/** Fact statements per page (a provider can make 300+ across a run). */
export const FACTS_PAGE_SIZE = 10;


function Stat({ label, value, suffix }) {
  return (
    <div className="text-xs">
      <div className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
        {value == null ? '—' : `${value}${suffix || ''}`}
      </div>
    </div>
  );
}

function factIcon(f) {
  if (f.is_hallucination) {
    return <span role="img" aria-label="Hallucination" title="Hallucination">🔴</span>;
  }
  if (f.is_accurate === false) {
    return <span role="img" aria-label="Inaccurate" title="Inaccurate">❌</span>;
  }
  if (f.is_accurate === true) {
    return <span role="img" aria-label="Accurate" title="Accurate">✅</span>;
  }
  return <span role="img" aria-label="No baseline" title="No baseline" className="text-gray-400">—</span>;
}

export default function ProviderDetailPanel({
  clientId,
  runId,
  providerId,
  providerName,
  scorecardRow,
  runPending = false,
  onClose,
}) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();
  useFocusTrap(dialogRef);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        requestAnimationFrame(() => prev.focus());
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Coverage says why a section is empty. It also resolves the provider id: a model change gives
  // a provider a new id between runs, so the id in the URL can be stale while the display name
  // still matches. The content calls wait for it (an empty id disables them) so a stale id never
  // fires a request that is discarded a moment later; a coverage error falls back to the URL id.
  const coverageQuery = useAimProviderCoverage(clientId, runId);
  const coverageSettled = !isPending(coverageQuery);
  const coverage = coverageFor(coverageQuery.data, providerId, providerName);
  const effectiveProviderId = coverageSettled ? (coverage?.provider_id ?? providerId) : '';

  const categoriesQuery = useAimProviderCategories(clientId, effectiveProviderId, runId);
  const factsQuery = useAimProviderFacts(clientId, effectiveProviderId, runId);
  const citationsQuery = useAimProviderCitations(clientId, effectiveProviderId, runId);
  const { data: categoriesRaw } = categoriesQuery;
  const { data: factsRaw } = factsQuery;
  const { data: citationsRaw } = citationsQuery;
  // `runPending`: opened straight from the URL, the panel can mount before the page has picked a
  // run. Every hook is disabled until then, and a disabled hook is not pending, so without it the
  // sections would flash their empty text for a run that has not resolved yet.
  const lCats = runPending || !coverageSettled || isPending(categoriesQuery);
  const lFacts = runPending || !coverageSettled || isPending(factsQuery);
  const lCits = runPending || !coverageSettled || isPending(citationsQuery);
  // Coverage is the authority on why a provider has nothing; the scorecard row stands in for it only
  // while coverage has no row for this provider (still loading, errored, or the provider isn't in it).
  const rowFailed = isCollectionFailed(scorecardRow);
  const noData = allSectionsFailed(coverage) || (!coverage && rowFailed);
  const stat = (value) => (rowFailed ? null : value);
  const emptyText = (section, generic) => sectionEmptyMessage(section, coverage?.[section]?.status, coverage) ?? generic;

  // Both Supabase RPCs wrap their rows in a one-element array; normalize before render.
  const categories = providerCategoryRows(categoriesRaw);
  // Memoized: usePagedRows returns to page 1 whenever its row array changes
  // identity, and providerFactsView builds a fresh array on every call.
  const factsView = useMemo(() => providerFactsView(factsRaw), [factsRaw]);
  const {
    page: factsPage, setPage: setFactsPage, pageCount: factsPageCount,
    visibleRows: visibleFacts, totalItems: factsTotal,
  } = usePagedRows(factsView.facts || [], FACTS_PAGE_SIZE);
  const citations = providerCitationRows(citationsRaw);

  const color = colorFor(providerName);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40 flex"
    >
      <button
        type="button"
        className="flex-1 bg-black/40 cursor-default"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside className="w-full sm:w-[920px] bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-800 overflow-y-auto">
        <header className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2">
            <ProviderColorDot displayName={providerName} size={12} />
            <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
              {providerName || 'Provider'}
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-5 text-gray-900 dark:text-gray-100">
          {noData ? (
            <AimEmpty message={NO_DATA_MESSAGE} dismissible={false} />
          ) : (
            <>
              {/* Header summary */}
              <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4" style={{ borderLeftColor: color.hex, borderLeftWidth: 4 }}>
                <div className="grid grid-cols-4 gap-3">
                  <Stat label="Mention" value={stat(scorecardRow?.mention_pct != null ? Number(scorecardRow.mention_pct).toFixed(1) : null)} suffix="%" />
                  <Stat label="Accuracy" value={stat(scorecardRow?.accuracy_pct != null ? Number(scorecardRow.accuracy_pct).toFixed(1) : null)} suffix="%" />
                  <Stat label="Sentiment" value={stat(scorecardRow?.sentiment ?? null)} />
                  <Stat label="Hallucinations" value={stat(scorecardRow?.hallucinations ?? null)} />
                </div>
              </section>

              {/* Mention by category */}
              <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Mention by category</h3>
                {!lCats && categories.length > 0 && categories.every((c) => c.mention_pct == null) && (
                  // Say which metric is on screen — a share of this provider's
                  // mentions is not the same as a per-category mention rate.
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Share of this provider&apos;s mentions, and the mention count, per category.
                  </p>
                )}
                {lCats ? (
                  <AimLoading height={120} />
                ) : !Array.isArray(categories) || categories.length === 0 ? (
                  <AimEmpty message={emptyText('categories', 'No category data for this provider.')} />
                ) : (
                  <div className="space-y-1.5">
                    {categories.map((c) => {
                      const label = c.category_display || prettifyCategory(c.category);
                      // `mention_pct` is a real 0–100 rate when the payload supplies
                      // one. Under the current Supabase RPC shape it does not — that
                      // payload carries counts, so the value falls back to a share of
                      // this provider's mentions. The subtitle below the heading says
                      // which of the two is on screen.
                      const isRate = c.mention_pct != null;
                      const value = isRate ? c.mention_pct : c.share_pct;
                      return (
                        <div key={c.category} className="flex items-center gap-3 text-sm">
                          <span className="w-40 truncate text-gray-700 dark:text-gray-300" title={label}>{label}</span>
                          {/* Colour tracks the value on the 0–100 mention-rate scale
                              (RatingColorBar / bucketFor): ≥90 green down to <40 red.
                              Note the caveat on `isRate` above — when the payload
                              only carries counts, the value being coloured is a share
                              of this provider's mentions, not a mention rate. */}
                          <div className="flex-1"><RatingColorBar value={value} /></div>
                          {c.count != null && (
                            <span className="w-10 text-right tabular-nums text-xs text-gray-500 dark:text-gray-400">{c.count}</span>
                          )}
                          <span className="w-14 text-right tabular-nums text-xs">{value == null ? '—' : `${Number(value).toFixed(0)}%`}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Fact statements — one row per statement, not per fact */}
              <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Fact statements</h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
                  Every claim this provider made about a recorded fact, once per response — so the same
                  fact recurs when several responses mention it. Hallucinations and inaccuracies are
                  listed first.
                </p>
                {lFacts ? (
                  <AimLoading height={120} />
                ) : factsView.mode === 'empty' ? (
                  <AimEmpty message={emptyText('facts', 'No fact data for this provider.')} />
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                    {visibleFacts.map((f, idx) => (
                      <div key={`${f.fact_key}-${idx}`} className="flex items-start gap-3 py-1.5">
                        <span className="w-6 text-center">{factIcon(f)}</span>
                        <span className="w-44 text-gray-700 dark:text-gray-300 truncate" title={f.fact_key_display || f.fact_key}>{f.fact_key_display || f.fact_key}</span>
                        <div className="flex-1 text-xs">
                          <div className="text-gray-900 dark:text-gray-100">{f.fact_value_stated || <span className="italic text-gray-400">(not stated)</span>}</div>
                          {f.ground_truth && (
                            <div className="text-gray-500 dark:text-gray-400">Truth: {f.ground_truth}</div>
                          )}
                        </div>
                        <div className="text-xs">
                          {f.is_hallucination && <span className="text-red-600 dark:text-red-400 font-semibold">HALLUCINATION</span>}
                          {!f.is_hallucination && f.is_accurate === false && <span className="text-amber-600 dark:text-amber-400">Inaccurate</span>}
                          {!f.is_hallucination && f.is_accurate === true && <span className="text-emerald-600 dark:text-emerald-400">Accurate</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!lFacts && factsView.mode !== 'empty' && (
                  <AimPagination
                    page={factsPage}
                    pageCount={factsPageCount}
                    onPage={setFactsPage}
                    totalItems={factsTotal}
                    label="statement"
                  />
                )}
              </section>

              {/* Citations used */}
              <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Citations used</h3>
                {lCits ? (
                  <AimLoading height={120} />
                ) : !Array.isArray(citations) || citations.length === 0 ? (
                  <AimEmpty message={emptyText('citations', 'No citations found for this provider.')} />
                ) : (
                  <ol className="text-sm space-y-1">
                    {citations.map((c, idx) => (
                      <li key={`${c.cited_domain}-${idx}`} className="flex items-center justify-between gap-2">
                        <span className="truncate">
                          {idx + 1}. <span className={c.is_client_site ? 'font-semibold text-emerald-700 dark:text-emerald-400' : ''}>{c.cited_domain}</span>
                          {c.is_client_site && <span className="ml-1 text-xs">⭐</span>}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{c.citations} time{c.citations === 1 ? '' : 's'}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
